import { createStore } from "vuex";
import proctoring from "./useProctoringStore";
import examEligibility from "./examEligibilityStore";
import axios from "axios";
import createPersistedState from "vuex-persistedstate";
import SecureLS from "secure-ls";
import { resetThemeColor } from "@/utils/themeColor";
import { resolveActiveRole, deriveDashboardAccess } from "@/utils/roleAccess";
import {
  DEFAULT_LOGIN_MODE,
  endpointForLoginMode,
  routeForLoginMode,
} from "@/utils/loginModes";

const ls = new SecureLS({ isCompression: false });

function decodeTokenExp(token) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  const decoded = JSON.parse(jsonPayload);

  return new Date(decoded.exp * 1000);
}

// The media cookie lasts MEDIA_COOKIE_MAX_AGE (4h) and so does the access token, so a
// session can hold a live token and a lapsed cookie. Re-mint a little before that.
const MEDIA_COOKIE_REFRESH_AFTER_MS = 3.5 * 60 * 60 * 1000;

function modeForAccess(dashboardAccess) {
  return dashboardAccess && dashboardAccess.length ? "admin" : "student";
}

export default createStore({
  modules: {
    proctoring,
    examEligibility,
  },

  state: {
    access: null,
    refresh: null,
    user: null,
    roles: [],
    dashboardAccess: [],
    activeRole: null,
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    // When the API last set the media cookie. Persisted, because the cookie is httpOnly
    // and JS cannot ask whether it exists.
    mediaCookieAt: null,
    tokenExp: null,
    refreshTokenExp: null,
    josh_id: null,
    loginError: null,
    is_face: false,
    is_nri: false,
    isSsoLogin: false,

    loginMode: DEFAULT_LOGIN_MODE,
    themeColors: {},
    sessionExpired: false,
  },

  getters: {
    isAuthenticated: (state) => state.isAuthenticated,
    user: (state) => state.user,
    access: (state) => state.access,
    refresh: (state) => state.refresh,
    roles: (state) => state.roles,
    dashboardAccess: (state) => state.dashboardAccess,
    activeRole: (state) => state.activeRole,
    themeColors: (state) => state.themeColors,
    getAccessToken: (state) => state.accessToken,
    getRefreshToken: (state) => state.refreshToken,
    getTokenExp: (state) => state.tokenExp,
    getRefreshTokenExp: (state) => state.refreshTokenExp,
    josh_id: (state) => state.josh_id,
    login_error: (state) => state.loginError,
    is_face: (state) => state.is_face,
    is_nri: (state) => state.is_nri,
    isSsoLogin: (state) => state.isSsoLogin,
    loginMode: (state) => state.loginMode,
    sessionExpired: (state) => state.sessionExpired,
  },

  mutations: {
    setLoggedInInfo(state, payload) {
      const userData = payload.data;

      state.isAuthenticated = true;

      state.access = userData.access;
      state.refresh = userData.refresh;

      state.user = {
        id: userData.user_id,
        student_profile_id: userData.student_profile_id,
        exam_guidelines: userData.exam_guidelines,
        full_name: userData.full_name,
        email: userData.email,
        phone: userData.phone,
        face_reference: userData.user_image,
        // APPLICANT is an entrance-exam candidate: no programme, batch or semester,
        // and the API returns null for each. null profile_type means staff.
        profile_type: userData.profile_type ?? null,
        enrollment_number: userData.enrollment_number ?? null,
        application_number: userData.application_number ?? null,
      };

      state.roles = userData.roles || [];
      state.dashboardAccess = deriveDashboardAccess(userData);
      state.activeRole = resolveActiveRole(
        state.dashboardAccess,
        state.activeRole
      );
      state.is_face = userData.is_face || false;
      state.is_nri = userData.is_nri || false;
    },

    setActiveRole(state, role) {
      if (state.dashboardAccess.includes(role)) {
        state.activeRole = role;
      }
    },

    SET_EXAM_GUIDELINES_ACKNOWLEDGED(state) {
      if (state.user) {
        state.user.exam_guidelines = true;
      }
    },

    setMediaCookieAt(state, at) {
      state.mediaCookieAt = at;
    },
    setLogout(state) {
      state.mediaCookieAt = null;
      state.access = null;
      state.refresh = null;
      state.user = null;
      state.roles = [];
      state.dashboardAccess = [];
      state.activeRole = null;
      state.isAuthenticated = false;
      state.accessToken = null;
      state.refreshToken = null;
      state.tokenExp = null;
      state.refreshTokenExp = null;
      state.josh_id = null;
      state.loginError = null;
      state.is_face = false;
      state.is_nri = false;
      state.isSsoLogin = false;
      resetThemeColor();

      localStorage.removeItem("exam_id");
      localStorage.removeItem("exam_name");
      localStorage.removeItem("room_id");
      localStorage.removeItem("room_name");
      localStorage.removeItem("exam_attempt_id");
    },
    setAccessToken(state, token) {
      state.accessToken = token;
    },
    setRefreshToken(state, token) {
      state.refreshToken = token;
    },
    setExpDate(state, date) {
      state.tokenExp = date;
    },
    setRefreshTokenExp(state, date) {
      state.refreshTokenExp = date;
    },
    setJoshInfo(state, data) {
      state.josh_id = data;
    },
    setLoginError(state, data) {
      state.loginError = data;
    },
    setIsFace(state, isFace) {
      state.is_face = isFace;
    },
    setIsNri(state, isNri) {
      state.is_nri = isNri;
    },
    updateAccessToken(state, token) {
      state.access = token;
      state.accessToken = token;
    },
    setUserFaceReference(state, faceReference) {
      if (state.user) {
        state.user.face_reference = faceReference;
      }
    },
    setSsoLogin(state, value) {
      state.isSsoLogin = value;
    },
    setLoginMode(state, mode) {
      state.loginMode = mode || DEFAULT_LOGIN_MODE;
    },
    setThemeColor(state, { role, color }) {
      state.themeColors = { ...state.themeColors, [role]: color };
    },
    setSessionExpired(state, value) {
      state.sessionExpired = value;
    },
  },

  actions: {
    async loginWithPassword({ commit, dispatch }, { credentials, mode }) {
      try {
        // withCredentials so the browser stores the media cookie the API sets here.
        // Cross-origin responses have their Set-Cookie ignored without it, and question
        // images would silently fail to load with nothing visible server-side.
        // `mode` stays out of the body - it picks the form, the API never sees it.
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}${endpointForLoginMode(mode)}`,
          credentials,
          { withCredentials: true }
        );

        commit("setLoginMode", mode);
        commit("setMediaCookieAt", Date.now());
        commit("setLoggedInInfo", response.data);
        commit("setAccessToken", response.data.data.access);
        commit("setRefreshToken", response.data.data.refresh);
        commit("setSsoLogin", false);
        if (response.data.data.josh_id != null) {
          commit("setJoshInfo", response.data.data.josh_id);
        }

        dispatch("saveToken");

        return response.data;
      } catch (error) {
        const data = error.response?.data;
        const message = data?.message || data?.error?.detail || "Login failed";

        commit("setLoginError", message);

        return {
          status: error.response?.status,
          message,
          reason: data?.error?.reason || null,
        };
      }
    },
    async sendStudentOtp(_, { email }) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/login/send-otp/`,
          { email }
        );
        return response.data;
      } catch (error) {
        console.error("Send OTP Error:", error);
        return error.response?.data ?? null;
      }
    },

    async loginWithOtp({ commit, dispatch, state }, { email, otp }) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/login/verify-otp/`,
          { email, otp }
        );
        commit("setLoggedInInfo", response.data);
        commit("setLoginMode", modeForAccess(state.dashboardAccess));
        commit("setAccessToken", response.data.data.access);
        commit("setRefreshToken", response.data.data.refresh);
        commit("setSsoLogin", false);
        if (response.data.data.josh_id != null) {
          commit("setJoshInfo", response.data.data.josh_id);
        }
        dispatch("saveToken");
        return response.data;
      } catch (error) {
        console.error("OTP Login Error:", error);
        return error.response?.data ?? null;
      }
    },

    async loginWithSsoToken({ commit, dispatch, state }, token) {
      try {
        // withCredentials: this call mints a token pair, so it is the SSO flow's login and
        // the API sets the media cookie on it. See loginWithPassword.
        const response = await axios.get(
          `${process.env.VUE_APP_API_ROOT}/auth/me/`,
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          }
        );

        commit("setMediaCookieAt", Date.now());
        commit("setLoggedInInfo", response.data);
        commit("setLoginMode", modeForAccess(state.dashboardAccess));
        commit("setAccessToken", response.data.data.access);
        commit("setRefreshToken", response.data.data.refresh);
        commit("setSsoLogin", true);
        if (response.data.data.josh_id != null) {
          commit("setJoshInfo", response.data.data.josh_id);
        }

        dispatch("saveToken");

        return response.data;
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error?.detail ||
          error.response?.data?.detail ||
          "SSO login failed";

        commit("setLoginError", message);

        return {
          status: error.response?.status,
          message,
        };
      }
    },
    logout({ commit, state }, { expired = false } = {}) {
      // Read before setLogout runs and handed back to the caller: every caller pushes
      // its route after awaiting this, so reading the store at that point is too late.
      const loginRoute = routeForLoginMode(state.loginMode);

      // Tokens live in this store, so clearing it ends the session locally. The media
      // cookie is held by the browser, so it has to be dropped server-side or the next
      // user of a shared machine inherits it. Fire-and-forget: a failed logout call must
      // never leave someone stuck on a page they have already left.
      axios
        .post(
          `${process.env.VUE_APP_API_ROOT}/auth/logout/`,
          {},
          { withCredentials: true }
        )
        .catch(() => {});

      commit("setLogout");
      commit("setExpDate", null);
      commit("setSessionExpired", expired);

      return loginRoute;
    },
    saveToken({ state, commit }) {
      const token = state.accessToken;
      if (token) {
        try {
          commit("setExpDate", decodeTokenExp(token));
        } catch (err) {
          console.error("Failed to decode access token JWT:", err);
        }
      }

      const refreshToken = state.refreshToken;
      if (refreshToken) {
        try {
          commit("setRefreshTokenExp", decodeTokenExp(refreshToken));
        } catch (err) {
          console.error("Failed to decode refresh token JWT:", err);
        }
      }
    },
    /**
     * Media authenticates from a cookie the API sets on login and refresh, never from the
     * bearer token - an <img> cannot send a header. A session that predates that change,
     * or one whose cookie lapsed while its equally-long access token did not, holds no
     * cookie: every image and attachment 401s, with nothing to see server-side and no
     * recovery until the token happens to expire. One refresh mints a fresh cookie.
     */
    async ensureMediaSession({ state, dispatch }) {
      if (!state.accessToken) return;
      if (!(state.refresh || state.refreshToken)) return;

      const age = state.mediaCookieAt
        ? Date.now() - state.mediaCookieAt
        : Infinity;
      if (age < MEDIA_COOKIE_REFRESH_AFTER_MS) return;

      await dispatch("refreshAccessToken");
    },

    async refreshAccessToken({ state, commit, dispatch }) {
      const refresh = state.refresh || state.refreshToken;
      if (!refresh) return null;

      // Always ask the server — it's the source of truth on whether the
      // refresh token is still valid, not our locally decoded JWT exp
      // (client clock skew / stale persisted state shouldn't end a session
      // the server would otherwise still honor).
      try {
        // Renews the media cookie alongside the access token - see loginWithPassword.
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/refresh/`,
          { refresh },
          { withCredentials: true }
        );

        if (response.data && response.data.access) {
          commit("updateAccessToken", response.data.access);
          commit("setMediaCookieAt", Date.now());
          dispatch("saveToken");
          return response.data.access;
        }

        dispatch("logout", { expired: true });
        return null;
      } catch (error) {
        // No response means the request never reached the server, so nothing
        // revoked the session -- keep the tokens and let the caller retry.
        if (!error.response) {
          console.warn(
            "Token refresh could not reach the server; keeping session",
            error.message
          );
          return null;
        }

        console.error("Token refresh failed:", error);
        dispatch("logout", { expired: true });
        return null;
      }
    },
  },

  plugins: [
    createPersistedState({
      key: "exam-app",
      storage: {
        getItem: (key) => {
          try {
            return ls.get(key);
          } catch (error) {
            console.warn(
              "Corrupted persisted state, resetting to defaults",
              error
            );
            try {
              ls.remove(key);
            } catch (_removeError) {
              // ignore — falling through to a fresh, unauthenticated state either way
            }
            return null;
          }
        },
        setItem: (key, value) => ls.set(key, value),
        removeItem: (key) => ls.remove(key),
      },
      paths: [
        "access",
        "refresh",
        "user",
        "roles",
        "dashboardAccess",
        "activeRole",
        "isAuthenticated",
        "accessToken",
        "refreshToken",
        "tokenExp",
        "refreshTokenExp",
        "is_face",
        "is_nri",
        "isSsoLogin",
        "loginMode",
        "themeColors",
      ],
    }),
  ],
});
